// src/pages/admin/EstimateEditor.js
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../lib/firebase";

function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function computeTotals({ items, discount, shipping, tax }) {
  const safeItems = Array.isArray(items) ? items : [];

  const itemsSubtotal = round2(
    safeItems.reduce((sum, it) => {
      const qty = num(it.qty, 0);
      const unit = num(it.unitPrice, 0);
      return sum + qty * unit;
    }, 0)
  );

  let discountAmount = 0;
  if (discount?.type === "percent") {
    discountAmount = round2(itemsSubtotal * (clamp(num(discount.value, 0), 0, 100) / 100));
  } else {
    discountAmount = round2(num(discount?.value, 0));
  }
  discountAmount = clamp(discountAmount, 0, itemsSubtotal);

  const afterDiscount = round2(Math.max(0, itemsSubtotal - discountAmount));

  let shippingAmount = 0;
  if (shipping?.type === "percent") {
    shippingAmount = round2(afterDiscount * (clamp(num(shipping.value, 0), 0, 100) / 100));
  } else {
    shippingAmount = round2(num(shipping?.value, 0));
  }
  shippingAmount = Math.max(0, shippingAmount);

  const preTaxSubtotal = round2(afterDiscount + shippingAmount);

  let taxAmount = 0;
  if (tax?.type === "percent") {
    taxAmount = round2(preTaxSubtotal * (clamp(num(tax.value, 0), 0, 100) / 100));
  } else {
    taxAmount = round2(num(tax?.value, 0));
  }
  taxAmount = Math.max(0, taxAmount);

  const grandTotal = round2(preTaxSubtotal + taxAmount);

  return {
    itemsSubtotal,
    discountAmount,
    shippingAmount,
    preTaxSubtotal,
    taxAmount,
    grandTotal,
  };
}

function regionLabel(country) {
  const c = (country || "").toLowerCase().trim();
  if (c === "canada" || c.includes("canada")) return "Province";
  if (c === "ireland" || c.includes("ireland")) return "County";
  if (
    c === "united states" ||
    c === "united states of america" ||
    c === "usa" ||
    c === "us" ||
    c.includes("united states")
  )
    return "State";
  return "Region";
}

export default function EstimateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [title, setTitle] = useState("");
  const [publicMessage, setPublicMessage] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  const [clientId, setClientId] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientOrganization, setClientOrganization] = useState("");

  const [addr, setAddr] = useState({
    line1: "",
    line2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "",
  });

  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState({ type: "amount", value: 0 });
  const [shipping, setShipping] = useState({ type: "amount", value: 0 });
  const [tax, setTax] = useState({ type: "percent", value: 0 });

  const [clientSearch, setClientSearch] = useState("");
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const ref = collection(db, "clients");
    const qy = query(ref, orderBy("createdAt", "desc"), limit(300));

    const unsub = onSnapshot(
      qy,
      (snap) => setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setClients([])
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!id) return;

    const ref = doc(db, "quotes", id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const q = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setEstimate(q);
        setLoading(false);

        if (q) {
          setTitle(q.title || "");
          setPublicMessage(q.publicMessage || "");
          setInternalNotes(q.internalNotes || "");
          setClientId(q.clientId || null);
          setClientName(q.clientName || "");
          setClientEmail(q.clientEmail || "");
          setClientPhone(q.clientPhone || "");
          setClientOrganization(q.clientOrganization || "");

          setAddr({
            line1: q.clientAddress?.line1 || "",
            line2: q.clientAddress?.line2 || "",
            city: q.clientAddress?.city || "",
            region: q.clientAddress?.region || "",
            postalCode: q.clientAddress?.postalCode || "",
            country: q.clientAddress?.country || "",
          });

          setItems(Array.isArray(q.items) ? q.items : []);
          setDiscount(q.discount || { type: "amount", value: 0 });
          setShipping(q.shipping || { type: "amount", value: 0 });
          setTax(q.tax || { type: "percent", value: 0 });
        }
      },
      () => {
        setEstimate(null);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [id]);

  const liveTotals = useMemo(
    () => computeTotals({ items, discount, shipping, tax }),
    [items, discount, shipping, tax]
  );

  const clientResults = useMemo(() => {
    const needle = clientSearch.trim().toLowerCase();
    if (!needle) return [];

    return clients
      .filter((c) => {
        const blob = [c.name, c.organization].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(needle);
      })
      .slice(0, 10);
  }, [clientSearch, clients]);

  function updateItem(idx, patch) {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  function addItem() {
    setItems((prev) => [...prev, { name: "New Item", desc: "", qty: 1, unitPrice: 0 }]);
  }

  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  function importClient(c) {
    setClientId(c.id);
    setClientName(c.name || "");
    setClientEmail(c.email || "");
    setClientPhone(c.phone || "");
    setClientOrganization(c.organization || "");
    setAddr({
      line1: c.addressLine1 || "",
      line2: c.addressLine2 || "",
      city: c.city || "",
      region: c.region || "",
      postalCode: c.postalCode || "",
      country: c.country || "",
    });
    setClientSearch("");
  }

  async function saveEstimate(e) {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    try {
      const totals = computeTotals({ items, discount, shipping, tax });

      await updateDoc(doc(db, "quotes", id), {
        docType: "estimate",
        title: title.trim(),
        publicMessage,
        internalNotes,

        clientId,
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        clientPhone: clientPhone.trim(),
        clientOrganization: clientOrganization.trim(),

        clientAddress: {
          line1: addr.line1.trim(),
          line2: addr.line2.trim(),
          city: addr.city.trim(),
          region: addr.region.trim(),
          postalCode: addr.postalCode.trim(),
          country: addr.country.trim(),
        },

        items: (Array.isArray(items) ? items : []).map((it) => ({
          name: (it.name || "").trim(),
          desc: (it.desc || "").trim(),
          qty: num(it.qty, 0),
          unitPrice: num(it.unitPrice, 0),
        })),

        discount: {
          type: discount?.type === "percent" ? "percent" : "amount",
          value: num(discount?.value, 0),
        },

        shipping: {
          type: shipping?.type === "percent" ? "percent" : "amount",
          value: num(shipping?.value, 0),
        },

        tax: {
          type: tax?.type === "amount" ? "amount" : "percent",
          value: num(tax?.value, 0),
        },

        totals,
        updatedAt: serverTimestamp(),
      });
    } finally {
      setSaving(false);
    }
  }

  async function deleteEstimate() {
    if (!id) return;
    const ok = window.confirm("Delete this estimate? This cannot be undone.");
    if (!ok) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "quotes", id));
      navigate("/admin/estimates");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) return <p className="muted">Loading...</p>;

  if (!estimate) {
    return (
      <div>
        <p className="muted">Estimate not found.</p>
        <Link className="btn" to="/admin/estimates">
          Back
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>{estimate.quoteNumber || "Estimate"}</h2>
          <div className="micro muted">Edit estimate</div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link className="btn" to={`/admin/estimates/${estimate.id}`}>
            View
          </Link>
          <button className="btn" type="button" onClick={deleteEstimate} disabled={saving || deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </button>
          <Link className="btn" to="/admin/estimates">
            Back
          </Link>
        </div>
      </div>

      <form onSubmit={saveEstimate} className="form" style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 8 }}>Header</h3>
        <label className="field">
          <span className="micro muted">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
          />
        </label>

        <h3 style={{ margin: "18px 0 8px" }}>Client</h3>

        <div style={{ marginBottom: 12 }}>
          <label className="field">
            <span className="micro muted">Search clients to import</span>
            <input
              type="text"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.15)",
                background: "rgba(255,255,255,0.8)",
              }}
            />
          </label>
          {clientSearch.trim() && (
            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
              {clientResults.length === 0 && <div className="micro muted">No clients found.</div>}
              {clientResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="btn"
                  onClick={() => importClient(c)}
                  disabled={saving}
                  style={{ justifyContent: "flex-start" }}
                >
                  {c.name || "Client"}{c.organization ? ` - ${c.organization}` : ""}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <label className="field">
            <span className="micro muted">Client name</span>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              disabled={saving}
            />
          </label>

          <label className="field">
            <span className="micro muted">Client email</span>
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              disabled={saving}
            />
          </label>

          <label className="field">
            <span className="micro muted">Client phone</span>
            <input
              type="text"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              disabled={saving}
            />
          </label>

          <label className="field">
            <span className="micro muted">Organization</span>
            <input
              type="text"
              value={clientOrganization}
              onChange={(e) => setClientOrganization(e.target.value)}
              disabled={saving}
            />
          </label>
        </div>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <label className="field">
            <span className="micro muted">Address line 1</span>
            <input
              type="text"
              value={addr.line1}
              onChange={(e) => setAddr((p) => ({ ...p, line1: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <span className="micro muted">Address line 2</span>
            <input
              type="text"
              value={addr.line2}
              onChange={(e) => setAddr((p) => ({ ...p, line2: e.target.value }))}
              disabled={saving}
            />
          </label>
        </div>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <label className="field">
            <span className="micro muted">City</span>
            <input
              type="text"
              value={addr.city}
              onChange={(e) => setAddr((p) => ({ ...p, city: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <span className="micro muted">{regionLabel(addr.country)}</span>
            <input
              type="text"
              value={addr.region}
              onChange={(e) => setAddr((p) => ({ ...p, region: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <span className="micro muted">Postal code</span>
            <input
              type="text"
              value={addr.postalCode}
              onChange={(e) => setAddr((p) => ({ ...p, postalCode: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <span className="micro muted">Country</span>
            <input
              type="text"
              value={addr.country}
              onChange={(e) => setAddr((p) => ({ ...p, country: e.target.value }))}
              disabled={saving}
            />
          </label>
        </div>

        <h3 style={{ margin: "18px 0 8px" }}>Items</h3>

        <datalist id="estimate-item-suggestions">
          <option value="Chalice" />
          <option value="Ciborium" />
          <option value="Paten" />
          <option value="Monstrance" />
          <option value="Pyx" />
          <option value="Thurible" />
          <option value="Bells" />
          <option value="Candlestick" />
          <option value="Processional Cross" />
          <option value="Crucifix" />
          <option value="Sanctuary Lamp" />
          <option value="Easter Candlestick" />
          <option value="Bookstand" />
          <option value="Brass Stand" />
          <option value="Tabernacle" />
          <option value="Tabernacle Door" />
        </datalist>

        <div style={{ display: "grid", gap: 12 }}>
          {items.length == 0 && <p className="muted">No items yet. Add one below.</p>}

          {items.map((it, idx) => (
            <div
              key={idx}
              style={{
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 14,
                padding: 12,
                background: "rgba(255,255,255,0.7)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong>Item #{idx + 1}</strong>
                <button type="button" className="btn" onClick={() => removeItem(idx)} disabled={saving}>
                  Remove
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 10,
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  marginTop: 10,
                }}
              >
                <label className="field">
                  <span className="micro muted">Item title</span>
                  <input
                    type="text"
                    list="estimate-item-suggestions"
                    value={it.name || ""}
                    onChange={(e) => updateItem(idx, { name: e.target.value })}
                    disabled={saving}
                  />
                </label>

                <label className="field">
                  <span className="micro muted">Quantity</span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={it.qty ?? 0}
                    onChange={(e) => updateItem(idx, { qty: e.target.value })}
                    disabled={saving}
                  />
                </label>

                <label className="field">
                  <span className="micro muted">Unit price</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={it.unitPrice ?? 0}
                    onChange={(e) => updateItem(idx, { unitPrice: e.target.value })}
                    disabled={saving}
                  />
                </label>

                <div className="micro muted" style={{ alignSelf: "center", textAlign: "right" }}>
                  Line Total: <strong>{money(num(it.qty, 0) * num(it.unitPrice, 0))}</strong>
                </div>
              </div>

              <label className="field" style={{ marginTop: 10 }}>
                <span className="micro muted">Description</span>
                <textarea
                  rows="3"
                  value={it.desc || ""}
                  onChange={(e) => updateItem(idx, { desc: e.target.value })}
                  disabled={saving}
                />
              </label>
            </div>
          ))}

          <button type="button" className="btn primary" onClick={addItem} disabled={saving}>
            Add Item
          </button>
        </div>

        <h3 style={{ margin: "18px 0 8px" }}>Pricing</h3>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 12,
              background: "rgba(255,255,255,0.7)",
            }}
          >
            <div className="micro muted" style={{ marginBottom: 6 }}>Discount</div>
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "120px 1fr" }}>
              <select
                value={discount.type}
                onChange={(e) => setDiscount((p) => ({ ...p, type: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              >
                <option value="amount">Amount</option>
                <option value="percent">Percent</option>
              </select>

              <input
                type="number"
                step="0.01"
                min="0"
                value={discount.value ?? 0}
                onChange={(e) => setDiscount((p) => ({ ...p, value: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              />
            </div>

            <div className="micro muted" style={{ marginTop: 10 }}>
              Discount Amount: <strong>{money(liveTotals.discountAmount)}</strong>
            </div>
          </div>

          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 12,
              background: "rgba(255,255,255,0.7)",
            }}
          >
            <div className="micro muted" style={{ marginBottom: 6 }}>Shipping</div>

            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "120px 1fr" }}>
              <select
                value={shipping.type}
                onChange={(e) => setShipping((p) => ({ ...p, type: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              >
                <option value="amount">Amount</option>
                <option value="percent">Percent</option>
              </select>

              <input
                type="number"
                step="0.01"
                min="0"
                value={shipping.value ?? 0}
                onChange={(e) => setShipping((p) => ({ ...p, value: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              />
            </div>

            <div className="micro muted" style={{ marginTop: 10 }}>
              Shipping Amount: <strong>{money(liveTotals.shippingAmount)}</strong>
            </div>
          </div>

          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 12,
              background: "rgba(255,255,255,0.7)",
            }}
          >
            <div className="micro muted" style={{ marginBottom: 6 }}>Tax</div>

            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "120px 1fr" }}>
              <select
                value={tax.type}
                onChange={(e) => setTax((p) => ({ ...p, type: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              >
                <option value="percent">Percent</option>
                <option value="amount">Amount</option>
              </select>

              <input
                type="number"
                step="0.01"
                min="0"
                value={tax.value ?? 0}
                onChange={(e) => setTax((p) => ({ ...p, value: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              />
            </div>

            <div className="micro muted" style={{ marginTop: 10 }}>
              Tax Amount: <strong>{money(liveTotals.taxAmount)}</strong>
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: 14,
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 14,
            padding: 14,
            background: "rgba(255,255,255,0.7)",
            maxWidth: 520,
          }}
        >
          <h4 style={{ marginTop: 0, marginBottom: 10 }}>Totals</h4>

          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Items Subtotal</span>
              <span>{money(liveTotals.itemsSubtotal)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Discount</span>
              <span>- {money(liveTotals.discountAmount)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Shipping</span>
              <span>{money(liveTotals.shippingAmount)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Pre-Tax Subtotal</span>
              <span>{money(liveTotals.preTaxSubtotal)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Tax</span>
              <span>{money(liveTotals.taxAmount)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 6, fontSize: 18 }}>
              <span>Total</span>
              <span>{money(liveTotals.grandTotal)}</span>
            </div>
          </div>
        </div>

        <h3 style={{ margin: "18px 0 8px" }}>Notes</h3>

        <label className="field">
          <span className="micro muted">Public message</span>
          <textarea
            rows="4"
            value={publicMessage}
            onChange={(e) => setPublicMessage(e.target.value)}
            disabled={saving}
          />
        </label>

        <label className="field">
          <span className="micro muted">Internal notes</span>
          <textarea
            rows="4"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            disabled={saving}
          />
        </label>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn primary" disabled={saving}>
            {saving ? "Saving..." : "Save Estimate"}
          </button>
        </div>
      </form>
    </div>
  );
}
